import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNotificationMutations, useNotifications } from "@/api/notification";
import { buttonClass } from "@/components/base/buttonStyles";
import { Modal, TextButton } from "@/pages/admin/master/_components/ui";

/**
 * 담당자 알림 목록 (REQ-N-01, 결정 #9).
 *
 * 대시보드 화면이 따로 없으므로 헤더에 텍스트로 붙인다. 배지·아이콘 없이 "알림 3" 처럼
 * 글자로만 알린다(§UI 1). 알림을 누르면 그 송장·배차 화면으로 바로 간다 —
 * 목록만 보여 주고 찾아가게 하면 알림이 일을 줄여 주지 못한다.
 */

const REF_PATHS: Record<string, (id: number) => string> = {
  invoice: (id) => `/admin/invoice/${id}`,
  dispatch: (id) => `/admin/dispatch/${id}/operation`,
  dispatch_trip: () => "/admin/invoice",
  driver_dispute: () => "/admin/drivers/disputes",
  vehicle_change_request: () => "/admin/drivers/vehicle-changes",
};

export default function NotificationPanel() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const query = useNotifications("admin", { size: 30 });
  const mutations = useNotificationMutations("admin");

  const unread = query.data?.unread ?? 0;
  const items = query.data?.items ?? [];

  const go = (refType: string | null | undefined, refId: number | null | undefined) => {
    if (!refType || !refId) return;
    const path = REF_PATHS[refType]?.(refId);
    if (!path) return;
    setOpen(false);
    navigate(path);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={buttonClass("secondary", "small")}
      >
        알림{unread > 0 && <span className="ml-1 text-gray-900">{unread}</span>}
      </button>

      {open && (
        <Modal
          title="알림"
          onClose={() => setOpen(false)}
          footer={
            <TextButton
              onClick={() => mutations.markRead.mutate([])}
              disabled={unread === 0 || mutations.markRead.isPending}
            >
              모두 읽음
            </TextButton>
          }
        >
          {items.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-400">알림이 없습니다</p>
          ) : (
            items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => go(item.ref_type, item.ref_id)}
                className="block w-full border-b border-gray-100 py-3 text-left last:border-b-0 hover:bg-gray-50"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span
                    className={`text-sm ${item.is_read ? "text-gray-500" : "font-medium text-gray-900"}`}
                  >
                    {item.title}
                  </span>
                  <span className="shrink-0 text-xs text-gray-400">{item.notif_type}</span>
                </div>
                {item.message && (
                  <p className="mt-0.5 text-xs leading-relaxed text-gray-500">{item.message}</p>
                )}
              </button>
            ))
          )}
        </Modal>
      )}
    </>
  );
}
